
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../services/supabaseClient';
import { ApiService } from '../services/api';
import { Vehicle } from '../types';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import Modal from '../components/Modal';
import { 
  Car, 
  Plus, 
  Search, 
  Filter, 
  Edit2, 
  Loader2, 
  AlertCircle,
  Save,
  Trash2,
  AlertTriangle,
  Upload,
  Tag,
  Lock
} from 'lucide-react';

interface VehicleFormData {
  vrm: string;
  make: string;
  model: string;
  colour: string;
  status: 'Active' | 'Inactive';
  notes: string;
  tags: string;
  // Owner information (for agency model)
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  insurance_provider: string;
  insurance_policy_number: string;
  insurance_status: string;
  insurance_expiry_date: string;
  insurance_doc_url: string;
  mot_doc_url: string;
  tax_doc_url: string;
}

const initialFormState: VehicleFormData = {
  vrm: '',
  make: '',
  model: '',
  colour: '',
  status: 'Active',
  notes: '',
  tags: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  insurance_provider: '',
  insurance_policy_number: '',
  insurance_status: '',
  insurance_expiry_date: '',
  insurance_doc_url: '',
  mot_doc_url: '',
  tax_doc_url: ''
};

const formatMaybeDate = (raw?: string | null) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateOnly = (raw?: string | null) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return raw;
  return d.toISOString().slice(0, 10);
};

const getComplianceBadgeClass = (dueDate?: string | null, status?: string | null) => {
  const normalizedStatus = String(status || '').toLowerCase();
  if (normalizedStatus.includes('expired') || normalizedStatus.includes('invalid')) {
    return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
  }
  if (dueDate) {
    const due = new Date(dueDate);
    if (Number.isFinite(due.getTime())) {
      const days = Math.floor((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (days < 0) {
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      }
      if (days <= 30) {
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800';
      }
      return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800';
    }
  }
  return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
};

const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const { checkAccess, promptUpgrade } = useSubscription();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State (Add/Edit)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<VehicleFormData>(initialFormState);
  
  // DVLA Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [dvlaData, setDvlaData] = useState<Partial<Vehicle> | null>(null);
  const hasDvlaLookup = checkAccess('dvlaLookup');

  // Modal State (Delete)
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [dispatchStatusByVehicle, setDispatchStatusByVehicle] = useState<Record<string, { blocked: boolean; openCriticalCount: number; reason: string | null }>>({});

  // Import State
  const [isImporting, setIsImporting] = useState(false);
  const [importReport, setImportReport] = useState<{ success: number; errors: string[] } | null>(null);
  const [isImportReportOpen, setIsImportReportOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<{ percent: number; message: string } | null>(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getVehicles();
      setVehicles(data || []);
      if (data && data.length > 0) {
        const statuses = await Promise.all(
          data.map(async (vehicle) => {
            try {
              const status = await ApiService.getVehicleDispatchStatus(vehicle.id);
              return [vehicle.id, status] as const;
            } catch {
              return [vehicle.id, { blocked: false, openCriticalCount: 0, reason: null }] as const;
            }
          })
        );
        setDispatchStatusByVehicle(Object.fromEntries(statuses));
      } else {
        setDispatchStatusByVehicle({});
      }
    } catch (err: any) {
      console.error('Error fetching vehicles:', err);
      const msg = getErrorMessage(err);
      if (msg === 'User not authenticated') {
        navigate('/login');
        return;
      }
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // --- Add / Edit Handlers ---

  const handleOpenModal = (vehicle?: Vehicle) => {
    if (vehicle) {
      setEditingId(vehicle.id);
      setFormData({
        vrm: vehicle.vrm || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
        colour: vehicle.colour || '',
        status: vehicle.status || 'Active',
        notes: vehicle.notes || '',
        tags: vehicle.tags ? vehicle.tags.join(', ') : '',
        owner_name: vehicle.owner_name || '',
        owner_email: vehicle.owner_email || '',
        owner_phone: vehicle.owner_phone || '',
        insurance_provider: vehicle.insurance_provider || '',
        insurance_policy_number: vehicle.insurance_policy_number || '',
        insurance_status: vehicle.insurance_status || '',
        insurance_expiry_date: toDateOnly(vehicle.insurance_expiry_date) || '',
        insurance_doc_url: vehicle.insurance_doc_url || '',
        mot_doc_url: vehicle.mot_doc_url || '',
        tax_doc_url: vehicle.tax_doc_url || ''
      });
      setDvlaData({
        year_of_manufacture: vehicle.year_of_manufacture,
        fuel_type: vehicle.fuel_type,
        engine_capacity: vehicle.engine_capacity,
        co2_emissions: vehicle.co2_emissions,
        marked_for_export: vehicle.marked_for_export,
        tax_status: vehicle.tax_status,
        tax_due_date: vehicle.tax_due_date,
        mot_status: vehicle.mot_status,
        mot_expiry_date: vehicle.mot_expiry_date
      });
    } else {
      setEditingId(null);
      setFormData(initialFormState);
      setDvlaData(null);
    }
    setIsModalOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') !== '1') return;

    setEditingId(null);
    setFormData(initialFormState);
    setIsModalOpen(true);
    params.delete('new');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormState);
    setEditingId(null);
    setError(null);
    setDvlaData(null);
  };

  const handleLookup = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!hasDvlaLookup) {
      promptUpgrade();
      return;
    }

    if (formData.vrm.length < 2) {
      addToast("Please enter a valid VRM", "error");
      return;
    }

    setIsLookingUp(true);
    try {
      const data = await ApiService.lookupVehicle(formData.vrm);
      if (data) {
        // Store DVLA data in a temporary state to be saved with the vehicle
        setDvlaData({
          year_of_manufacture: (data as any)?.yearOfManufacture,
          fuel_type: (data as any)?.fuelType,
          engine_capacity: (data as any)?.engineCapacity,
          co2_emissions: (data as any)?.co2Emissions,
          marked_for_export: (data as any)?.markedForExport,
          tax_status: (data as any)?.taxStatus,
          tax_due_date: toDateOnly((data as any)?.taxDueDate) ?? undefined,
          mot_status: (data as any)?.motStatus,
          mot_expiry_date: toDateOnly((data as any)?.motExpiryDate) ?? undefined
        });

        setFormData(prev => ({
          ...prev,
          make: data.make || '',
          model: data.model || '',
          colour: data.colour || ''
        }));

        // Build comprehensive vehicle info
        const makeModel = [data.make, data.model].filter(Boolean).join(' ').trim();
        const colour = data.colour ? `(${data.colour})` : '';
        const year = (data as any)?.yearOfManufacture ? `${(data as any).yearOfManufacture}` : '';
        const fuelType = (data as any)?.fuelType || '';
        const engineCapacity = (data as any)?.engineCapacity ? `${(data as any).engineCapacity}cc` : '';
        const co2 = (data as any)?.co2Emissions ? `${(data as any).co2Emissions}g/km CO₂` : '';

        const motStatus = (data as any)?.motStatus as string | undefined;
        const motExpiry = (data as any)?.motExpiryDate as string | undefined;
        const taxStatus = (data as any)?.taxStatus as string | undefined;
        const taxDue = (data as any)?.taxDueDate as string | undefined;
        const markedForExport = (data as any)?.markedForExport as boolean | undefined;

        // Format MOT info
        const mot =
          motStatus && motExpiry
            ? `MOT: ${motStatus} (exp ${new Date(motExpiry).toLocaleDateString('en-GB')})`
            : motStatus
              ? `MOT: ${motStatus}`
              : null;

        // Format Tax info
        const tax =
          taxStatus && taxDue
            ? `Tax: ${taxStatus} (due ${new Date(taxDue).toLocaleDateString('en-GB')})`
            : taxStatus
              ? `Tax: ${taxStatus}`
              : null;

        // Build vehicle specs line
        const specs = [year, fuelType, engineCapacity, co2].filter(Boolean).join(' • ');

        // Determine toast type based on status
        const isAllGood = (motStatus ? motStatus.toLowerCase() === 'valid' : true) && (taxStatus ? taxStatus.toLowerCase() === 'taxed' : true);
        const type = markedForExport || !isAllGood ? 'info' : 'success';

        // Build toast message
        const headline = makeModel ? `✅ DVLA: ${makeModel} ${colour}`.trim() : '✅ DVLA match found';
        const exportWarning = markedForExport ? '⚠️ Marked for export' : null;
        const parts = [headline, specs, mot, tax, exportWarning].filter(Boolean);

        addToast(parts.join(' • '), type);
      } else {
        addToast("Vehicle not found", "error");
      }
    } catch (err) {
      addToast(getErrorMessage(err), "error");
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate owner information
    if (formData.owner_name && formData.owner_name.trim() !== '') {
      if (!formData.owner_email && !formData.owner_phone) {
        setError('If you provide an owner name, you must also provide at least an email or phone number.');
        addToast('Owner contact details required', 'error');
        setIsSubmitting(false);
        return;
      }

      // Validate email format if provided
      if (formData.owner_email && formData.owner_email.trim() !== '') {
        // More robust email validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(formData.owner_email.trim())) {
          setError('Please provide a valid email address for the owner.');
          addToast('Invalid owner email format', 'error');
          setIsSubmitting(false);
          return;
        }
      }

      // Validate UK phone format if provided
      if (formData.owner_phone && formData.owner_phone.trim() !== '') {
        const cleanPhone = formData.owner_phone.replace(/\s+/g, '');
        // UK phone format: 07xxx xxxxxx (11 digits) or +447xxx xxxxxx (13-14 chars)
        // Mobile: 07[0-9]{9} | Landline: 0[1-9][0-9]{8,9}
        const phoneRegex = /^(\+44[1-9]\d{9,10}|0[1-9]\d{8,9})$/;
        if (!phoneRegex.test(cleanPhone)) {
          setError('Please provide a valid UK phone number (e.g., 07123456789, 02012345678, or +447123456789).');
          addToast('Invalid owner phone format', 'error');
          setIsSubmitting(false);
          return;
        }
      }
    }

    try {
      const payload: Partial<Vehicle> = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t.length > 0),
        insurance_expiry_date: formData.insurance_expiry_date || undefined,
        insurance_provider: formData.insurance_provider || undefined,
        insurance_policy_number: formData.insurance_policy_number || undefined,
        insurance_status: formData.insurance_status || undefined,
        insurance_doc_url: formData.insurance_doc_url || undefined,
        mot_doc_url: formData.mot_doc_url || undefined,
        tax_doc_url: formData.tax_doc_url || undefined,
        // Include DVLA data if available
        ...(dvlaData || {})
      };

      if (editingId) {
        payload.id = editingId;
      }

      await ApiService.saveVehicle(payload);
      addToast(editingId ? "Vehicle updated successfully" : "Vehicle created successfully", 'success');
      
      await fetchVehicles();
      handleCloseModal();
    } catch (err: any) {
      const msg = getErrorMessage(err);
      if (msg === 'User not authenticated') {
        navigate('/login');
        return;
      }

      // Check if it's a duplicate VRM error
      if (msg.includes('already exists in your fleet')) {
        setError(msg);
        addToast(msg, 'error');

        // Find the existing vehicle with this VRM
        const existingVehicle = vehicles.find(v =>
          v.vrm.replace(/\s+/g, '').toUpperCase() === formData.vrm.replace(/\s+/g, '').toUpperCase()
        );

        if (existingVehicle) {
          // Highlight the existing vehicle in the list
          setTimeout(() => {
            const element = document.getElementById(`vehicle-${existingVehicle.id}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              element.classList.add('ring-2', 'ring-red-500', 'ring-offset-2');
              setTimeout(() => {
                element.classList.remove('ring-2', 'ring-red-500', 'ring-offset-2');
              }, 3000);
            }
          }, 500);
        }
      } else {
        setError(msg);
        addToast(`Failed to save: ${msg}`, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Delete Handlers ---

  const handleDeleteClick = (vehicle: Vehicle) => {
    setVehicleToDelete(vehicle);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!vehicleToDelete) return;
    setIsDeleting(true);
    try {
      await ApiService.deleteVehicle(vehicleToDelete.id);
      addToast('Vehicle deleted successfully', 'success');
      await fetchVehicles();
      setIsDeleteModalOpen(false);
      setVehicleToDelete(null);
    } catch (err: any) {
      const msg = getErrorMessage(err);
      addToast(`Failed to delete: ${msg}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Import Handlers ---

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportProgress({ percent: 0, message: 'Starting…' });
    try {
      const result = await ApiService.importVehicles(file, {
        onProgress: (p) => setImportProgress(p)
      });
      setImportReport(result);
      setIsImportReportOpen(true);
      if (result.errors.length) {
        addToast(`Imported ${result.success} vehicles with ${result.errors.length} issue(s)`, 'info');
      } else {
        addToast(`Successfully imported ${result.success} vehicles`, 'success');
      }
      if (result.success > 0) fetchVehicles();
    } catch (err) {
      addToast("Failed to import vehicles", 'error');
    } finally {
      setIsImporting(false);
      setImportProgress(null);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const q = searchTerm.toLowerCase();
    return (
      (v.vrm || '').toLowerCase().includes(q) ||
      (v.make || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q) ||
      v.tags?.some(tag => (tag || '').toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Vehicles</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Manage your fleet vehicles and tags.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleImportClick}
            disabled={isImporting}
            className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            {isImporting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {isImporting && importProgress?.percent ? `Importing ${importProgress.percent}%` : 'Import CSV'}
          </button>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept=".csv" 
            className="hidden" 
          />
          <button 
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Vehicle
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-900 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium">Error</h3>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search vehicles, makes, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-900"
          />
        </div>
        <div className="relative w-full sm:w-48">
          <Filter className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <select className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none appearance-none bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">VRM</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Details</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Tags</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Status</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">Owner</th>
                  <th className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredVehicles.length > 0 ? (
                  filteredVehicles.map((vehicle) => (
                    <tr
                      key={vehicle.id}
                      id={`vehicle-${vehicle.id}`}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all duration-300"
                    >
                      <td className="px-6 py-4 font-bold text-slate-900 dark:text-white font-mono">
                        {vehicle.vrm}
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                        <div className="font-medium">
                          {(vehicle.make || '').trim()} {(vehicle.model || '').trim()}
                        </div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">
                          {vehicle.colour || ''}
                        </div>
                        {(vehicle.year_of_manufacture ||
                          vehicle.fuel_type ||
                          vehicle.engine_capacity ||
                          vehicle.co2_emissions ||
                          vehicle.mot_status ||
                          vehicle.tax_status) && (
                          <div className="mt-2 flex flex-col gap-2">
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {[
                                vehicle.year_of_manufacture ? `${vehicle.year_of_manufacture}` : null,
                                vehicle.fuel_type ? `${vehicle.fuel_type}` : null,
                                vehicle.engine_capacity ? `${vehicle.engine_capacity}cc` : null,
                                vehicle.co2_emissions ? `${vehicle.co2_emissions}g/km CO₂` : null
                              ].filter(Boolean).join(' • ')}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {vehicle.mot_status && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getComplianceBadgeClass(vehicle.mot_expiry_date, vehicle.mot_status)}`}
                                >
                                  MOT: {vehicle.mot_status}
                                  {vehicle.mot_expiry_date ? ` (exp ${formatMaybeDate(vehicle.mot_expiry_date)})` : ''}
                                </span>
                              )}
                              {vehicle.tax_status && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getComplianceBadgeClass(vehicle.tax_due_date, vehicle.tax_status)}`}
                                >
                                  Tax: {vehicle.tax_status}
                                  {vehicle.tax_due_date ? ` (due ${formatMaybeDate(vehicle.tax_due_date)})` : ''}
                                </span>
                              )}
                              {vehicle.insurance_status && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getComplianceBadgeClass(vehicle.insurance_expiry_date, vehicle.insurance_status)}`}
                                >
                                  Insurance: {vehicle.insurance_status}
                                  {vehicle.insurance_expiry_date ? ` (exp ${formatMaybeDate(vehicle.insurance_expiry_date)})` : ''}
                                </span>
                              )}
                              {vehicle.marked_for_export && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800">
                                  Marked for export
                                </span>
                              )}
                              {dispatchStatusByVehicle[vehicle.id]?.blocked && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800"
                                  title={dispatchStatusByVehicle[vehicle.id]?.reason || 'Dispatch blocked by open critical defect.'}
                                >
                                  Dispatch blocked ({dispatchStatusByVehicle[vehicle.id]?.openCriticalCount})
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {vehicle.tags && vehicle.tags.length > 0 ? vehicle.tags.map((tag, i) => (
                             <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-900">
                               {tag}
                             </span>
                          )) : <span className="text-slate-400 text-xs italic">No tags</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          vehicle.status === 'Active'
                            ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                            : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                        }`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {vehicle.owner_name ? (
                          <div className="group relative">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800">
                              {vehicle.owner_name.length > 20 ? `${vehicle.owner_name.substring(0, 20)}...` : vehicle.owner_name}
                            </span>
                            {/* Tooltip with full owner details */}
                            <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10 w-64 p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                              <div className="text-xs space-y-1.5">
                                <div className="font-semibold text-slate-900 dark:text-white">{vehicle.owner_name}</div>
                                {vehicle.owner_email && (
                                  <div className="text-slate-600 dark:text-slate-400">
                                    <span className="font-medium">Email:</span> {vehicle.owner_email}
                                  </div>
                                )}
                                {vehicle.owner_phone && (
                                  <div className="text-slate-600 dark:text-slate-400">
                                    <span className="font-medium">Phone:</span> {vehicle.owner_phone}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500 text-xs italic">(No owner)</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button 
                          onClick={() => handleOpenModal(vehicle)}
                          className="p-2 text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                          title="Edit Vehicle"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          onClick={() => handleDeleteClick(vehicle)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all ml-1"
                          title="Delete Vehicle"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                      <div className="flex flex-col items-center justify-center">
                        <Car className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                        <p className="font-medium text-slate-900 dark:text-white">No vehicles found</p>
                        <p className="text-sm mt-1">Add your first vehicle or import a CSV.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingId ? 'Edit Vehicle' : 'Add New Vehicle'}
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                VRM (Registration) <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  value={formData.vrm}
                  onChange={(e) => setFormData({ ...formData, vrm: e.target.value.toUpperCase() })}
                  className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none uppercase font-mono text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                  placeholder="AB12 CDE"
                />
                <button
                  type="button"
                  onClick={handleLookup}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border flex items-center gap-2 transition-colors ${
                    hasDvlaLookup 
                      ? 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-primary-500 hover:text-primary-600' 
                      : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-400'
                  }`}
                  title={hasDvlaLookup ? "Auto-fill from DVLA" : "Upgrade to Pro for DVLA Lookup"}
                >
                  {isLookingUp ? <Loader2 size={16} className="animate-spin" /> : hasDvlaLookup ? <Search size={16} /> : <Lock size={16} />}
                  <span className="hidden sm:inline">DVLA Lookup</span>
                </button>
              </div>
            </div>

            {!!(dvlaData && (
              dvlaData.year_of_manufacture ||
              dvlaData.fuel_type ||
              dvlaData.engine_capacity ||
              dvlaData.co2_emissions ||
              dvlaData.mot_status ||
              dvlaData.tax_status ||
              dvlaData.marked_for_export
            )) && (
              <div className="col-span-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">DVLA details</div>
                    <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {[
                        dvlaData.year_of_manufacture ? `${dvlaData.year_of_manufacture}` : null,
                        dvlaData.fuel_type || null,
                        dvlaData.engine_capacity ? `${dvlaData.engine_capacity}cc` : null,
                        dvlaData.co2_emissions ? `${dvlaData.co2_emissions}g/km CO₂` : null
                      ].filter(Boolean).join(' • ') || '—'}
                    </div>
                  </div>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                    Saved with vehicle
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {dvlaData.mot_status && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                        dvlaData.mot_status.toLowerCase() === 'valid'
                          ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                          : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                      }`}
                    >
                      MOT: {dvlaData.mot_status}
                      {dvlaData.mot_expiry_date ? ` (exp ${formatMaybeDate(dvlaData.mot_expiry_date)})` : ''}
                    </span>
                  )}
                  {dvlaData.tax_status && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                        dvlaData.tax_status.toLowerCase() === 'taxed'
                          ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                          : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                      }`}
                    >
                      Tax: {dvlaData.tax_status}
                      {dvlaData.tax_due_date ? ` (due ${formatMaybeDate(dvlaData.tax_due_date)})` : ''}
                    </span>
                  )}
                  {dvlaData.marked_for_export && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800">
                      Marked for export
                    </span>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="Ford"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="Transit"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Colour</label>
              <input
                type="text"
                value={formData.colour}
                onChange={(e) => setFormData({ ...formData, colour: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="White"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tags</label>
              <div className="relative">
                <Tag className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                  placeholder="e.g. Uber, PHV, Pool Car"
                />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Separate multiple tags with commas.</p>
            </div>

            <div className="col-span-2 pt-4 border-t border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Compliance Documents
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Capture insurance and evidence document links used for compliance checks.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Insurance Provider
              </label>
              <input
                type="text"
                value={formData.insurance_provider}
                onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="e.g. AXA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Policy Number
              </label>
              <input
                type="text"
                value={formData.insurance_policy_number}
                onChange={(e) => setFormData({ ...formData, insurance_policy_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="POL-12345"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Insurance Status
              </label>
              <select
                value={formData.insurance_status}
                onChange={(e) => setFormData({ ...formData, insurance_status: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              >
                <option value="">Unknown</option>
                <option value="active">Active</option>
                <option value="pending_renewal">Pending renewal</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Insurance Expiry Date
              </label>
              <input
                type="date"
                value={formData.insurance_expiry_date}
                onChange={(e) => setFormData({ ...formData, insurance_expiry_date: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Insurance Document URL
              </label>
              <input
                type="url"
                value={formData.insurance_doc_url}
                onChange={(e) => setFormData({ ...formData, insurance_doc_url: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                MOT Evidence URL
              </label>
              <input
                type="url"
                value={formData.mot_doc_url}
                onChange={(e) => setFormData({ ...formData, mot_doc_url: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="https://..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Tax Evidence URL
              </label>
              <input
                type="url"
                value={formData.tax_doc_url}
                onChange={(e) => setFormData({ ...formData, tax_doc_url: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="https://..."
              />
            </div>

            {/* Owner Information Section */}
            <div className="col-span-2 pt-4 border-t border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                Owner Information (for agency model)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                If you manage this vehicle on behalf of an owner, provide their contact details below.
              </p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Owner Name
              </label>
              <input
                type="text"
                value={formData.owner_name}
                onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="e.g. John Smith"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Owner Email
              </label>
              <input
                type="email"
                value={formData.owner_email}
                onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="owner@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Owner Phone
              </label>
              <input
                type="tel"
                value={formData.owner_phone}
                onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="07123 456789"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none text-slate-900 dark:text-white bg-white dark:bg-slate-800"
                placeholder="Additional details..."
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              type="button"
              onClick={handleCloseModal}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Vehicle
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Vehicle"
        maxWidth="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-900">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm font-medium">This action cannot be undone.</p>
          </div>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Are you sure you want to delete vehicle <span className="font-bold text-slate-900 dark:text-white">{vehicleToDelete?.vrm}</span>?
            This may affect existing tickets or hires linked to this vehicle.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className="px-4 py-2 text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-70"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
              Delete Vehicle
            </button>
          </div>
        </div>
      </Modal>

      {/* Import Results Modal */}
      <Modal
        isOpen={isImportReportOpen}
        onClose={() => setIsImportReportOpen(false)}
        title="CSV Import Results"
        maxWidth="max-w-lg"
      >
        <div className="p-6 space-y-4">
          {isImporting && importProgress && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm text-slate-700 dark:text-slate-200 mb-2">
                <span className="font-medium">{importProgress.message}</span>
                <span className="tabular-nums">{importProgress.percent}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-primary-600" style={{ width: `${importProgress.percent}%` }} />
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 rounded-lg p-4">
            <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div>
              <div className="font-medium text-slate-800 dark:text-white">
                Imported {importReport?.success ?? 0} vehicle(s)
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {importReport?.errors?.length ? `${importReport.errors.length} issue(s) found.` : 'No issues found.'}
              </div>
            </div>
          </div>

          {!!importReport?.errors?.length && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200">Issues</div>
              <div className="max-h-64 overflow-auto rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {importReport.errors.slice(0, 100).map((msg, i) => (
                    <li key={i} className="px-4 py-2 text-sm text-slate-700 dark:text-slate-200">
                      {msg}
                    </li>
                  ))}
                </ul>
              </div>
              {importReport.errors.length > 100 && (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Showing first 100 issues.
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => setIsImportReportOpen(false)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Vehicles;
